import { useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_GROUPS, ADMIN_SECTIONS, type AdminSectionKey } from "@/pages/portal/admin/config/sections";
import { useAdminCounters } from "@/pages/portal/admin/context/AdminCountersContext";
import ApiStatusBadge from "@/components/feature/ApiStatusBadge";
import NotificationBell from "@/pages/portal/components/NotificationBell";
import AdminAccessDenied from "@/pages/portal/admin/components/AdminAccessDenied";

interface AdminLayoutProps {
  active: AdminSectionKey;
  onSelect: (key: AdminSectionKey) => void;
  children: ReactNode;
}

/**
 * Dedicated shell for the standalone Admin Console at /admin.
 *
 * Completely independent from the Owner Portal — its own branding, its own
 * grouped navigation and its own access guard. It is intentionally not linked
 * anywhere in the owner-facing UI; admins reach it by visiting /admin directly.
 */
export default function AdminLayout({ active, onSelect, children }: AdminLayoutProps) {
  const { user, logout, isLoading, isDemoMode } = useAuth();
  const { counters } = useAdminCounters();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  /** Live counter shown as a badge on the matching nav item. */
  const badgeFor = (key: AdminSectionKey): number => {
    if (key === "crm-pipeline") return counters.openDeals;
    if (key === "invoices") return counters.unpaidInvoices;
    return 0;
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background-50">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          <p className="text-sm text-foreground-500">Loading admin console…</p>
        </div>
      </div>
    );
  }

  // Signed-out visitors must authenticate first.
  if (!user) return <Navigate to="/login" replace />;
  // Signed in but not an administrator → explain it with a friendly screen
  // instead of silently bouncing them to the owner dashboard.
  // Demo/preview mode is allowed so the console stays explorable; the live
  // backend still requires an admin JWT for every /api/admin call.
  if (!isDemoMode && user.role !== "admin") return <AdminAccessDenied />;

  function handleLogout() {
    logout();
    navigate("/login");
  }

  const initials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const activeSection = ADMIN_SECTIONS.find((s) => s.key === active);

  const Sidebar = () => (
    <aside className="flex h-full flex-col border-r border-background-200 bg-foreground-950 text-foreground-100">
      {/* Brand */}
      <div className="flex items-center justify-between border-b border-background-50/10 px-5 py-4">
        <Link to="/admin" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-500 text-foreground-950">
            <i className="ri-shield-user-line text-lg" />
          </span>
          <div>
            <p className="text-sm font-bold leading-tight">Coastal Bizz</p>
            <p className="text-[10px] uppercase tracking-widest text-foreground-400">Admin Console</p>
          </div>
        </Link>
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-400 hover:bg-background-50/10 hover:text-foreground-50 lg:hidden"
          aria-label="Close menu"
        >
          <i className="ri-close-line text-lg" />
        </button>
      </div>

      {/* Grouped navigation */}
      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-4">
        {ADMIN_GROUPS.map((group) => {
          const items = ADMIN_SECTIONS.filter((s) => s.group === group);
          if (items.length === 0) return null;
          return (
            <div key={group} className="mb-3">
              <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-widest text-foreground-500">
                {group}
              </p>
              {items.map((item) => {
                const isActive = active === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => { onSelect(item.key); setSidebarOpen(false); }}
                    className={`flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-left text-[13px] font-medium transition-colors ${
                      isActive
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-300 hover:bg-background-50/10 hover:text-foreground-50"
                    }`}
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {badgeFor(item.key) > 0 && (
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums ${
                          isActive ? "bg-background-50/25 text-background-50" : "bg-accent-500 text-foreground-950"
                        }`}
                      >
                        {badgeFor(item.key)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-background-50/10 p-3">
        <Link
          to="/"
          className="flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium text-foreground-300 transition-colors hover:bg-background-50/10 hover:text-foreground-50"
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-arrow-left-line text-base" />
          </span>
          Back to Directory
        </Link>
        <div className="mt-1 flex items-center gap-3 rounded-lg px-2 py-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-500 text-xs font-bold text-foreground-950">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-foreground-50">{user.name}</p>
            <p className="truncate text-[10px] text-foreground-400">{user.email}</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            title="Logout"
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-50/10 hover:text-foreground-50"
          >
            <i className="ri-logout-box-r-line text-base" />
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background-100">
      {/* Desktop sidebar */}
      <div className="hidden w-64 shrink-0 lg:block">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="absolute inset-0 bg-foreground-950/40" />
          <div className="absolute left-0 top-0 h-full w-64" onClick={(e) => e.stopPropagation()}>
            <Sidebar />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Admin top bar */}
        <div className="flex items-center gap-3 border-b border-background-200 bg-background-50 px-4 py-3 lg:px-8">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 lg:hidden"
            aria-label="Open menu"
          >
            <i className="ri-menu-line text-base" />
          </button>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-foreground-400">Admin Console</p>
            <h1 className="truncate font-heading text-sm font-bold text-foreground-950">
              {activeSection?.label ?? "Dashboard"}
            </h1>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <ApiStatusBadge />
            <NotificationBell />
            <span className="hidden items-center gap-1.5 rounded-full border border-accent-300 bg-accent-50 px-3 py-1.5 text-xs font-semibold text-accent-800 sm:inline-flex">
              <i className="ri-shield-check-line text-sm" />
              Administrator
            </span>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}